/**
 * WhatsApp deep links for the manager "say hi" step after accepting an
 * application. Pure — no DOM, no Convex — so the same normalisation serves the
 * MembersPanel and any export/agent tooling. One owner: do not re-derive
 * phone cleaning elsewhere.
 */

export const DEFAULT_WELCOME_MESSAGE =
  "Hey {first}! Good news — you're in for {community}. Your ticket is waiting: sign in at {url} and grab it. Can't wait to see you!";

const PLACEHOLDER = /\{(first|name|community|url)\}/g;

/**
 * Best-effort E.164 digits for wa.me. Free-text phone answers arrive in every
 * shape ("+351 912 345 678", "00351912345678", "912345678", "(917) 975-3389").
 * Rules, in order:
 *   - strip everything but digits; "00" international prefix → dropped
 *   - a leading "0" trunk prefix is dropped and the default country prepended
 *   - 9 digits starting with 9 → Portuguese mobile, default country prepended
 *   - 10 digits not starting with 0 → North American, "1" prepended
 *   - anything else is assumed to already carry its country code
 * Returns null when nothing usable remains (< 8 digits). `guessed` flags the
 * cases where a country code was inferred, so the UI can say "assumed +351".
 */
export function normalizeWhatsappNumber(
  raw: string | null | undefined,
  defaultCountry = '351'
): { digits: string; guessed: boolean } | null {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('0')) {
    return finish(defaultCountry + digits.replace(/^0+/, ''), true);
  }
  if (digits.length === 9 && digits.startsWith('9')) {
    return finish(defaultCountry + digits, true);
  }
  if (digits.length === 10 && !digits.startsWith('0')) {
    return finish('1' + digits, true);
  }
  return finish(digits, false);
}

function finish(digits: string, guessed: boolean) {
  return digits.length >= 8 && digits.length <= 15 ? { digits, guessed } : null;
}

/** First token of a display name, capitalised — what you'd write after "Hey". */
export function firstNameOf(name: string | null | undefined): string {
  const first = String(name ?? '').trim().split(/\s+/)[0] ?? '';
  return first ? first[0].toUpperCase() + first.slice(1) : '';
}

export function renderWelcomeMessage(
  template: string | null | undefined,
  vars: { first: string; name: string; community: string; url: string }
): string {
  const source = template && template.trim() ? template : DEFAULT_WELCOME_MESSAGE;
  return source.replace(PLACEHOLDER, (_m, key: keyof typeof vars) => vars[key] ?? '');
}

export function buildWhatsappUrl(digits: string, text: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}
